import React, { useState } from "react";
import { format, addMonths } from "date-fns";
import { CheckCircleIcon } from "@heroicons/react/outline";
import CustomerInfo from "../../../../../components/JewelleryPlans/common/CustomerInfo";

// Helper function to format address object to string
const formatAddress = (address) => {
  if (!address || typeof address !== "object") return "";

  const parts = [];
  if (address.label) parts.push(address.label);
  if (address.line1) parts.push(address.line1);
  if (address.line2) parts.push(address.line2);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.pinCode) parts.push(address.pinCode);

  return parts.join(", ");
};

const ReviewAndConfirm = ({ data, onBack, onSubmit, loading, error }) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signatureCaptured, setSignatureCaptured] = useState(false);

  const { customer, template, monthlyAmount, startDate, notes } = data;
  const maturityDate = addMonths(startDate, template.tenureMonths);
  const totalValue = monthlyAmount * template.tenureMonths;

  // Format customer data to ensure address is a string
  const formattedCustomer = customer
    ? {
        ...customer,
        address:
          typeof customer.address === "object"
            ? formatAddress(customer.address)
            : customer.address,
      }
    : null;

  const handleSubmit = () => {
    if (termsAccepted && signatureCaptured) {
      onSubmit();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-text mb-4">
          Review & Confirm Plan
        </h2>
        <p className="text-sm text-neutral-dark">
          Please review all details carefully before creating the plan.
        </p>
      </div>

      {/* Customer Details */}
      <div>
        <h3 className="text-sm font-medium text-text mb-2">Customer Details</h3>
        <CustomerInfo customer={formattedCustomer} />
      </div>

      {/* Plan Details */}
      <div className="bg-background border border-neutral-light rounded-lg p-4">
        <h3 className="text-sm font-medium text-text mb-3">Plan Details</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-2 border-b border-neutral-light">
            <span className="text-neutral-dark">Template:</span>
            <span className="font-medium">{template.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-light">
            <span className="text-neutral-dark">Plan Type:</span>
            <span className="font-medium">
              {template.type.replace("_", " ")}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-light">
            <span className="text-neutral-dark">Monthly Amount:</span>
            <span className="font-medium">
              ₹{Number(monthlyAmount).toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-light">
            <span className="text-neutral-dark">Tenure:</span>
            <span className="font-medium">{template.tenureMonths} months</span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-light">
            <span className="text-neutral-dark">Start Date:</span>
            <span className="font-medium">
              {format(startDate, "dd MMM yyyy")}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-neutral-light">
            <span className="text-neutral-dark">Maturity Date:</span>
            <span className="font-medium">
              {format(maturityDate, "dd MMM yyyy")}
            </span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-text font-medium">Total Investment:</span>
            <span className="text-lg font-semibold text-success">
              ₹{totalValue.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Benefits Summary */}
      <div className="bg-primary-light border border-primary rounded-lg p-4">
        <h3 className="text-sm font-medium text-white mb-2">
          Benefits & Features
        </h3>
        <ul className="text-xs text-gray-200 space-y-1">
          <li>• Grace period of {template.graceDays} days for payments</li>
          <li>• Flexible redemption options after maturity</li>
          {template.benefitType === "FREE_INSTALLMENT" && (
            <li>
              • Bonus installment after {template.benefitParams?.triggerAfterN}{" "}
              payments
            </li>
          )}
          {template.benefitType === "VA_DISCOUNT_CAP" && (
            <li>
              • VA capped at {template.benefitParams?.maxPercent}% on redemption
            </li>
          )}
          {template.benefitType === "MAKING_CHARGE_WAIVER" && (
            <li>
              • {template.benefitParams?.waiveChargesPercent}% off on making
              charges
            </li>
          )}
        </ul>
      </div>

      {/* Terms & Conditions */}
      <div className="border border-neutral-light rounded-lg p-4">
        <div className="flex items-start">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 text-primary focus:ring-primary border-neutral-light rounded"
          />
          <label className="ml-3 text-sm text-text">
            I have read and agree to the{" "}
            <a href="#" className="text-primary hover:text-primary-dark">
              terms and conditions
            </a>{" "}
            of the savings plan. I understand the payment schedule and
            redemption policies.
          </label>
        </div>
      </div>

      {/* Digital Signature */}
      <div className="border border-neutral-light rounded-lg p-4">
        <h4 className="text-sm font-medium text-text mb-2">
          Digital Signature
        </h4>
        <div className="bg-neutral-light border-2 border-dashed border-neutral-dark rounded-lg p-8 text-center">
          {signatureCaptured ? (
            <div className="flex flex-col items-center">
              <CheckCircleIcon className="h-12 w-12 text-success mb-2" />
              <p className="text-sm text-success">Signature captured</p>
              <button
                onClick={() => setSignatureCaptured(false)}
                className="mt-2 text-xs text-neutral-dark hover:text-text"
              >
                Re-capture
              </button>
            </div>
          ) : (
            <button
              onClick={() => setSignatureCaptured(true)}
              className="px-4 py-2 bg-background border border-neutral-light rounded-md text-sm font-medium text-text hover:bg-neutral-light"
            >
              Capture Signature
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-danger-light border border-danger rounded-lg p-4">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={loading}
          className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={!termsAccepted || !signatureCaptured || loading}
          className={`px-6 py-2 rounded-md text-sm font-medium
            ${
              termsAccepted && signatureCaptured && !loading
                ? "bg-success text-text-on-primary hover:bg-success-dark"
                : "bg-neutral-light text-neutral-dark cursor-not-allowed"
            }`}
        >
          {loading ? "Creating Plan..." : "Create Plan"}
        </button>
      </div>
    </div>
  );
};

export default ReviewAndConfirm;
